import { ChangeDetectionStrategy, Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BeforeRenderEventHandler,
  NForm,
  NFormComponent,
  NFormControlTemplateContext,
  NFormRecordRef,
  NFormValuesChange,
} from '@pebula/nform';
import { UnRx } from '@pebula/utils';
import { AsyncTracker, parseTemplatedKeysToString } from '@sg/utils';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import {
  SgEntityFetchAdapter,
  SgEntitySelectEntityFetchMap,
  SgEntitySelectFetchStore,
} from '../entity-select-fetch-store.service';

@Component({
  selector: 'sg-custom-entity-select-auto-complete',
  exportAs: 'sgCustomEntityAutoCompleteSelect',
  templateUrl: './custom-entity-select-auto-complete.component.html',
  styleUrls: ['./custom-entity-select-auto-complete.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@UnRx()
export class SgCustomEntitySelectAutoCompleteComponent implements OnDestroy {
  @Input() get ctx(): NFormControlTemplateContext {
    return this._ctx;
  }
  set ctx(value: NFormControlTemplateContext) {
    if (this._ctx !== value) {
      if (this._ctx) {
        UnRx.kill(this, this._ctx);
      }

      this._ctx = value;
      if (value) {
        value.fControl.valueChanges
          .pipe(startWith(value.fControl.value), distinctUntilChanged(), UnRx(this, value))
          .subscribe((obj) => {
            if (obj) {
              // Always process context when value changes to pick up model updates
              // Even if nForm already exists, the entity data may have changed
              this.processContext(value);
            } else {
              this.nForm$.next(undefined);
            }
          });
      }
    }
  }

  nForm$ = new BehaviorSubject<any>(undefined);
  disabledControls: string[] = [];
  tracker = new AsyncTracker();

  private _ctx: NFormControlTemplateContext;
  private entity: SgEntityFetchAdapter<any>;
  private entities: any[] = [];
  private nFormCmp: NFormComponent<any>;
  private parentNForm: NForm<any>;
  private nForm: { model: any }; // Simple wrapper with model property for template
  private originalHadEntity = false;

  constructor(private entityStore: SgEntitySelectFetchStore) {}

  ngAfterViewInit(): void {
    // BehaviorSubject will immediately emit the current value (if any) to subscribers
  }

  ngOnDestroy(): void {
    this.nForm$.complete();
  }

  beforeFormQueryRender(event: BeforeRenderEventHandler, nFormCmp: NFormComponent<any>): void {
    this.nFormCmp = nFormCmp;

    if (event.isRoot) {
      const refId = event.records.refId as NFormRecordRef<'entitySelectAutoComplete'>;
      const data: Partial<NFormRecordRef<'entitySelectAutoComplete'>['data']> = {
        onEntitiesFetched: (entities) => this.entities.push(...entities),
        entity: undefined,
      };
      refId.mergeData(data);

      // Note: this.nForm is now a simple wrapper { model: ... }, not an actual NForm
      // The form is managed by the pbl-nform component in the template
      if (this.nForm?.model) {
        const value = this.nForm.model;

        // Track if there was an original entity when the form loaded or reset
        if (value?.type && value?.refId) {
          this.originalHadEntity = true;
        }

        // Register the entity fetcher for the autocomplete field
        if (value?.type) {
          if (!this.tryFindEntity(value.type)) {
            this.nForm$.next(undefined);
          }
        }
      }
    }
  }

  valueChanges($event: NFormValuesChange): void {
    const obj = $event.reduce(
      (prev, curr) => {
        prev[curr.key] = curr;
        return prev;
      },
      {} as Record<'type' | 'refId' | 'label', NFormValuesChange[0]>
    );

    if (obj.type) {
      if (obj.type.previousValue !== null && obj.type.currentValue !== obj.type.previousValue) {
        this.entities = [];
      }
      if (this.tryFindEntity(obj.type.currentValue)) {
        if (!obj.refId && obj.type.previousValue) {
          this.nFormCmp.nForm.setValue('refId', null);
        }
        this.nFormCmp.records.refId.markAsChanged();
      } else {
        this.nForm$.next(undefined);
        return;
      }
    }

    if (obj.refId && obj.refId.currentValue) {
      if (this.entities && this.entity?.metadata) {
        const { idField: idKey, labelField } = this.entity.metadata;
        const entity = this.entities.find((entity) => entity[idKey] === obj.refId.currentValue);
        if (entity) {
          const entityLabelField = entity[labelField as string];
          const label = entityLabelField
            ? entityLabelField
            : parseTemplatedKeysToString(labelField as string[], entity);
          this.nFormCmp.nForm.setValue('label', label);
        }
      }
    }

    // After any value changes, commit to model and check if entity is complete
    if (this.nForm?.model && this.nFormCmp) {
      this.nFormCmp.nForm.commitToModel();

      const model = this.nForm.model;
      if (model.type && model.refId) {
        // Entity is complete, sync the model back to parent
        if (this.parentNForm && this.ctx) {
          const parentModel = this.parentNForm.model as any;

          if (parentModel) {
            // Update the parent model
            parentModel[this.ctx.item.name] = model;

            // Get the form control directly from the parent form group
            // (ctx.fControl might be stale after processContext creates a new control)
            const parentFormControl = this.parentNForm.form.get(this.ctx.item.name);
            if (parentFormControl) {
              parentFormControl.setValue(model, { emitEvent: false });
              parentFormControl.markAsDirty();
              parentFormControl.markAsTouched();
            }
          }
        }
      }
    }
  }

  attachEntity(): void {
    // Track if there was an entity value when we first attached
    // This helps us know if detaching should mark as dirty or not
    const currentValue = this.ctx.fControl.value;
    this.originalHadEntity = currentValue && (currentValue.type || currentValue.refId);

    // Get the entity factory from FormProp data, or create a plain object as fallback
    const entityFactory = this.ctx.item.data?.entityFactory;
    let emptyEntity;

    if (entityFactory && typeof entityFactory === 'function') {
      emptyEntity = entityFactory();
    } else {
      // Fallback to plain object (this will cause NForm serialization errors)
      emptyEntity = {
        type: undefined,
        refId: undefined,
        label: undefined,
      };
    }

    // Update the parent model
    this.ctx.nForm.model[this.ctx.item.name] = emptyEntity;

    // Update the form control with the new entity
    this.ctx.fControl.setValue(emptyEntity);
    this.ctx.fControl.markAsDirty();
    this.ctx.fControl.markAsTouched();

    // Now processContext should work with the new entity
    this.processContext(this.ctx);
  }

  detachEntity(): void {
    // Check if there was an actual value before detaching
    const hadValue = this.nForm?.model && (this.nForm.model.type || this.nForm.model.refId);

    // Create an empty FormControl to replace the entity
    const emptyControl = new FormControl(null);
    this.parentNForm.form.setControl(this.ctx.item.name, emptyControl);

    // Update the parent model
    if (this.parentNForm && this.parentNForm.model) {
      (this.parentNForm.model as any)[this.ctx.item.name] = null;
    }

    // Only mark as dirty if:
    // 1. There was a value in the form that's being removed, AND
    // 2. There was an original entity (removing it is a change)
    if (hadValue && this.originalHadEntity) {
      this.parentNForm.form.markAsDirty();
    } else if (!this.originalHadEntity) {
      // If there was no original entity and we're detaching,
      // we're back to the original state, so mark as pristine
      emptyControl.markAsPristine();
      emptyControl.markAsUntouched();
    }

    this.nForm = undefined;
    this.nForm$.next(undefined);
    this.originalHadEntity = false;
  }

  private processContext(context: NFormControlTemplateContext): void {
    this.parentNForm = context.nForm;

    // Get the value from the MODEL, not the form control
    const modelValue = context.nForm.model[context.item.name];

    // In Angular 17, we can't use childForm: true metadata due to serialization issues
    // So instead, we:
    // 1. Keep the parent form control as a simple FormControl (not FormGroup)
    // 2. Create an independent NForm for the entity (not a child of parent)
    // 3. Sync values between them manually

    // Check if modelValue is valid: must have a proper constructor (not plain Object)
    // Plain objects are no longer supported - use entityFactory in @FormProp data instead
    const isValidModel = modelValue && modelValue.constructor && modelValue.constructor.name !== 'Object';

    if (isValidModel) {
      // We have a valid model instance (must be @FormModel decorated class)
      // Ensure parent has a FormControl (not FormGroup) for this property
      if (!(context.fControl instanceof FormControl)) {
        context.nForm.form.setControl(context.item.name, new FormControl(modelValue));
      }

      // Emit an object with the model property for the template
      const formWrapper = { model: modelValue };
      this.nForm = formWrapper as any;
      this.nForm$.next(this.nForm);
    } else {
      // No valid model value
      this.nForm = undefined;
      this.nForm$.next(undefined);
    }
  }

  private tryFindEntity(entityKey: keyof SgEntitySelectEntityFetchMap): boolean {
    if (!this.entityStore.hasFetcher(entityKey)) {
      return false;
    }

    this.entity = this.entityStore.getFetcher(entityKey);
    if (!this.entity) {
      return false;
    }

    this.nFormCmp.records.refId.mergeData({ entity: this.entity.key });
    return true;
  }
}
